import { BadgeRow } from '../badges';
import { useEffect, useRef, useState } from 'react';
import { addonsApi, encodeCtx, formatSize, progressApi, setPlayQueue, stremioIdFor, useAddons, type PickPrefs, type StreamOption, type WatchCtx } from '../addons';
import { useApp } from '../context';
import { prefsNow } from '../prefs';
import type { PlexItem } from '../plex';
import { navigate } from '../router';

/**
 * Link naar de addon-speler; de sleutel verwijst naar een adres dat alleen de server kent.
 * `alternatives` = de andere bronnen in volgorde (reserves als deze niet werkt).
 */
export function playAddonStream(s: StreamOption, title: string, live = false, ctx?: WatchCtx, alternatives?: StreamOption[], opts: { replace?: boolean; tries?: number } = {}) {
  const q = new URLSearchParams({ key: s.key, title, src: `${s.addonName}${s.quality ? ` · ${s.quality}` : ''}` });
  if (s.webReady) q.set('web', '1');
  if (live) q.set('live', '1');
  // Wat er speelt: voor voortgang, Verder kijken en Trakt/SIMKL (niet bij live)
  if (ctx && !live) {
    q.set('ctx', encodeCtx(ctx));
    q.set('sa', s.addonName);
    q.set('sn', s.name);
  }
  if (alternatives && !live) {
    const list = [s, ...alternatives.filter((x) => x.key !== s.key)];
    setPlayQueue({ title, ctx, titleId: ctx?.id, list: list.filter((x) => !x.blocked || x.key === s.key), i: 0 });
    q.set('q', '1');
  }
  if (opts.tries) q.set('fo', String(opts.tries));
  navigate(`/addon-play?${q}`, { replace: opts.replace });
}

/** Korte labels uit de naam van een bron: klaar bij debrid, HDR, geluid. */
export function StreamTraits({ s }: { s: StreamOption }) {
  const t = s.traits;
  if (!t) return null;
  const tags = [
    t.cached === true ? 'Klaar' : t.cached === false ? 'Nog downloaden' : null,
    t.nl === 'audio' ? 'NL' : t.nl === 'subs' ? 'NL-subs' : null,
  ].filter(Boolean) as string[];
  // Beeld en geluid als badges (Badge Studio: welke, volgorde en stijl)
  const media = [
    /remux/i.test(`${s.name} ${s.title}`) ? 'Remux' : null,
    t.hdr === 'dv' ? 'Dolby Vision' : t.hdr === 'hdr' ? (/hdr10\+|hdr10plus/i.test(`${s.name} ${s.title}`) ? 'HDR10+' : 'HDR') : null,
    t.codec === 'hevc' ? 'HEVC' : t.codec === 'av1' ? 'AV1' : null,
    t.audio === 'atmos' ? 'Atmos' : t.audio === 'truehd' ? 'TrueHD' : t.audio === 'dts' ? 'DTS' : t.audio === 'ddp' ? 'DD+' : null,
    /\b7[ .]1\b/.test(`${s.name} ${s.title}`) ? '7.1' : /\b5[ .]1\b/.test(`${s.name} ${s.title}`) ? '5.1' : null,
  ].filter(Boolean) as string[];
  if (!tags.length && !media.length) return null;
  return (
    <span className="ad-traits">
      {tags.map((x) => <span key={x} className={`ad-trait${x === 'Klaar' ? ' is-ready' : x === 'Nog downloaden' ? ' is-wait' : ''}`}>{x}</span>)}
      <BadgeRow labels={media} place="sources" size="sm" />
    </span>
  );
}

/** Lijst met bronnen uit addons voor een type + Stremio-id. */
export function StreamList({ type, id, title, addonId, autoload = true, live = false, ctx }: { type: string; id: string; title: string; addonId?: string; autoload?: boolean; live?: boolean; ctx?: WatchCtx }) {
  const [streams, setStreams] = useState<StreamOption[] | null>(null);
  // Laatst gebruikte bron en waar je was (om die bovenaan te zetten en te hervatten)
  const [last, setLast] = useState<{ source: { addon: string; name: string } | null; position: number } | null>(null);
  useEffect(() => {
    setLast(null);
    if (!ctx || live) return;
    progressApi.resume(ctx.id, ctx.ratingKey).then((r) => setLast({ source: r.source, position: r.position })).catch(() => undefined);
  }, [ctx?.id, ctx?.ratingKey, live]);
  const [errors, setErrors] = useState<{ addon: string; message: string }[]>([]);
  const [load, setLoad] = useState(autoload);
  const [pick, setPick] = useState<PickPrefs | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [tick, setTick] = useState(0);
  const reload = () => setTick((n) => n + 1);
  // Addons die nog bezig waren (snelle start): even later stil opnieuw vragen, dan staan ze erbij
  const [pending, setPending] = useState<string[]>([]);
  const refills = useRef(0);
  useEffect(() => {
    if (!load) return;
    let off = false;
    if (!tick) {
      setStreams(null);
      refills.current = 0;
    }
    addonsApi.streams(type, id, addonId)
      .then((r) => {
        if (off) return;
        // De server zet ze al in volgorde van het kwaliteitsprofiel
        setStreams(r.streams);
        setPick(r.pick ?? null);
        setErrors(r.errors);
        setPending(r.pending ?? []);
        if (r.pending?.length && refills.current < 2) {
          refills.current += 1;
          window.setTimeout(() => !off && setTick((n) => n + 1), 5000);
        }
      })
      .catch(() => !off && setStreams([]));
    return () => {
      off = true;
    };
  }, [type, id, addonId, load, tick]);

  if (!load) return <button className="btn btn-sm" onClick={() => setLoad(true)}>Bronnen zoeken in addons</button>;
  if (!streams) return <p className="subtle" aria-busy="true">Bronnen zoeken in je addons…</p>;
  const isLast = (s: StreamOption) => !s.blocked && !!last?.source && s.addonName === last.source.addon && s.name === last.source.name;
  // Vastgezet gaat voor; anders de laatst gebruikte bron bovenaan
  const sorted = last?.source && !streams.some((x) => x.pinned) ? [...streams.filter(isLast), ...streams.filter((s) => !isLast(s))] : streams;
  const play = (s: StreamOption) => playAddonStream(s, title, live, ctx, live ? undefined : sorted);
  const best = sorted[0];
  // Per kwaliteit groeperen (4K, 1080p, 720p, overig); de groep met de beste bron eerst.
  // Geblokkeerde bronnen achteraan in hun groep (ze staan al onderaan in de volgorde).
  const perQuality = pick?.perQuality ?? 3;
  const groups: { q: string; items: StreamOption[] }[] = [];
  for (const st of sorted) {
    const q = st.quality === '4K' || st.quality === '1080p' || st.quality === '720p' ? st.quality : 'Overig';
    let g = groups.find((x) => x.q === q);
    if (!g) groups.push((g = { q, items: [] }));
    g.items.push(st);
  }
  const compact = !!pick?.autoPick && !live && !!best && !showAll;
  return (
    <div className="ad-streams">
      {!streams.length && !pending.length && <p className="subtle">Geen afspeelbare bronnen gevonden in je addons.</p>}
      {pending.length > 0 && <p className="subtle ad-pending" aria-live="polite">Nog bezig: {pending.join(', ')}</p>}
      {last && last.position > 60 && streams.length > 0 && <p className="ad-resume-hint">Je was bij {fmtClock(last.position)}: je kijkt verder waar je was.</p>}
      {compact && (
        <div className="ad-best">
          <button className="btn btn-primary ad-best-play" data-autofocus onClick={() => play(best)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4v16l13-8z" /></svg>
            {last && last.position > 60 ? 'Verder kijken' : 'Afspelen'}
          </button>
          <span className="ad-best-info">
            <strong>
              {best.quality ?? 'Beste bron'}{isLast(best) ? ' · laatst gebruikt' : ''}
              {best.verified && <span className="ad-verified" title="Vooraf getest: deze bron antwoordt"> ✓ Getest</span>}
            </strong>
            <span className="subtle">{[best.addonName, formatSize(best.size)].filter(Boolean).join(' · ')}</span>
            <StreamTraits s={best} />
            {best.why && best.why.length > 0 && (
              <details className="ad-why">
                <summary>Waarom deze bron?</summary>
                <ul>{best.why.map((w) => <li key={w}>{w}</li>)}</ul>
              </details>
            )}
          </span>
          {sorted.length > 1 && <button className="btn btn-ghost btn-sm" onClick={() => setShowAll(true)}>Zelf kiezen ({sorted.length})</button>}
        </div>
      )}
      {!compact && groups.map((g) => {
        const open = !!expanded[g.q];
        const shown = perQuality > 0 && !open ? g.items.slice(0, perQuality) : g.items;
        const rest = g.items.length - shown.length;
        return (
      <section key={g.q} className="ad-group" aria-label={`${g.q} (${g.items.length})`}>
        <h3 className="ad-group-head">
          <span>{g.q}</span>
          <span className="subtle">{g.items.length}</span>
          {open && perQuality > 0 && g.items.length > perQuality && (
            <button className="btn btn-ghost btn-sm" onClick={() => setExpanded((x) => ({ ...x, [g.q]: false }))}>Minder tonen</button>
          )}
        </h3>
      <ul className="ad-stream-list" data-focus-group>
        {shown.map((s) => (
          <li key={s.key} className={s.blocked ? 'is-blocked' : undefined}>
            <button className={`ad-stream${isLast(s) ? ' is-last' : ''}`} onClick={() => play(s)}>
              <span className="ad-stream-q">{s.quality ?? '—'}</span>
              <span className="ad-stream-body">
                <strong>{s.name}</strong>
                {s.title && <span className="subtle ad-stream-title">{s.title}</span>}
                {isLast(s) && <span className="ad-last">Laatst gebruikt</span>}
                <StreamTraits s={s} />
                {s.pinned && <span className="ad-last">Vastgezet</span>}
                {s.blocked && <span className="ad-blocked">{s.blocked.auto ? 'Overgeslagen (werkte niet)' : 'Geblokkeerd'}</span>}
              </span>
              <span className="ad-stream-meta subtle" title={s.webReady ? 'Speelt rechtstreeks af (bij debrid zonder de server)' : 'Je browser kan dit bestand niet zelf afspelen, dus de server verpakt het. In de apps speelt het rechtstreeks bij debrid.'}>
                {[s.addonName, formatSize(s.size), s.webReady ? 'rechtstreeks' : 'via server'].filter(Boolean).join(' · ')}
              </span>
            </button>
            {s.fp && !live && <StreamActions s={s} id={id} onChanged={reload} />}
          </li>
        ))}
      </ul>
        {rest > 0 && (
          <button className="btn btn-sm ad-more" onClick={() => setExpanded((x) => ({ ...x, [g.q]: true }))}>Meer {g.q} tonen ({rest})</button>
        )}
      </section>
        );
      })}
      {errors.map((e) => <p key={e.addon} className="subtle hint">Niet bereikbaar: {e.addon} ({e.message})</p>)}
    </div>
  );
}

/** Waarom deze bron, en vastzetten of blokkeren voor deze titel. */
function StreamActions({ s, id, onChanged }: { s: StreamOption; id: string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const act = async (action: 'pin' | 'unpin' | 'block' | 'unblock') => {
    setBusy(true);
    await addonsApi.choice(id, s.fp!, action, `${s.addonName} · ${s.name}`).catch(() => undefined);
    setBusy(false);
    onChanged();
  };
  return (
    <details className="ad-why ad-why-row">
      <summary>Waarom?</summary>
      {s.title && <p className="ad-why-file">{s.title}</p>}
      {s.why && s.why.length > 0 ? <ul>{s.why.map((w) => <li key={w}>{w}</li>)}</ul> : <p className="subtle">Geen bijzonderheden.</p>}
      <div className="ad-why-actions">
        {s.pinned
          ? <button className="btn btn-sm" disabled={busy} onClick={() => void act('unpin')}>Niet meer vastzetten</button>
          : <button className="btn btn-sm" disabled={busy || !!s.blocked} onClick={() => void act('pin')}>Vastzetten voor deze titel</button>}
        {s.blocked
          ? <button className="btn btn-sm" disabled={busy} onClick={() => void act('unblock')}>Weer toestaan</button>
          : <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void act('block')}>Blokkeren</button>}
      </div>
    </details>
  );
}

export function fmtClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** Op de detailpagina van een Plex-titel: extra bronnen uit je addons (volgens je volgorde van bronnen). */
export function AddonSources({ item }: { item: PlexItem }) {
  const { plex } = useApp();
  const addons = useAddons();
  const [sid, setSid] = useState<{ type: 'movie' | 'series'; id: string } | null | undefined>(undefined);

  const hasStreams = !!addons?.allowed && (!!addons.iptvVod || addons.addons.some((a) => a.kind === 'video' && a.streams && a.resources.includes('stream')));

  useEffect(() => {
    if (!hasStreams) return;
    if (item.type === 'episode' && item.grandparentRatingKey) {
      plex.metadata(item.grandparentRatingKey).then((show) => setSid(stremioIdFor(item, show.Guid))).catch(() => setSid(null));
    } else setSid(stremioIdFor(item));
  }, [item.ratingKey, hasStreams]);

  // Alvast zoeken zodra de pagina open is (de server onthoudt het even): dan staan de bronnen
  // meteen klaar als je op "Bronnen zoeken" of Afspelen drukt
  useEffect(() => {
    if (!sid || !addons || addons.sourceOrder === 'plex-only' || addons.sourceOrder === 'addons-first' || addons.sourceOrder === 'addons-only') return;
    if (!prefsNow().prefetchNext) return;
    const t = window.setTimeout(() => void addonsApi.streams(sid.type, sid.id).catch(() => undefined), 800);
    return () => window.clearTimeout(t);
  }, [sid?.id, addons?.sourceOrder]);

  if (!hasStreams || !addons || addons.sourceOrder === 'plex-only' || !sid) return null;
  const first = addons.sourceOrder === 'addons-first' || addons.sourceOrder === 'addons-only';
  return (
    <section className="row ad-sources" aria-label="Bronnen uit je addons">
      <h2>Bronnen uit je addons</h2>
      <StreamList
        type={sid.type}
        id={sid.id}
        title={item.type === 'episode' ? `${item.grandparentTitle} · ${item.title}` : item.title}
        autoload={first}
        ctx={{
          id: sid.id,
          type: sid.type,
          metaId: sid.id.split(':')[0],
          name: item.type === 'episode' ? item.grandparentTitle ?? item.title : item.title,
          epTitle: item.type === 'episode' ? item.title : undefined,
          season: item.type === 'episode' ? item.parentIndex : undefined,
          episode: item.type === 'episode' ? item.index : undefined,
          ratingKey: item.ratingKey,
        }}
      />
    </section>
  );
}
